import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { X, Eye, EyeOff, ArrowLeft, Mail, Lock, KeyRound, AlertTriangle, Check, Loader2, MapPin, User, Phone, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FloatingLabelInput } from './ui/FloatingLabelInput';
import { useToast } from '../hooks/use-toast';
import Stepper, { Step } from './ui/Stepper';
import KVKKCheckboxes, { validateKVKKConsents } from './KVKKCheckboxes';
import { SearchableSelect } from './ui/SearchableSelect';
import citiesData from '../utils/cities.json';
import { useAnalytics } from '../utils/analytics';

// --- Helper Functions & Constants ---
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const TCKN_REGEX = /^[1-9][0-9]{10}$/;

const validateEmail = (email) => EMAIL_REGEX.test(email);
const validateName = (name) => {
    // Allows Turkish characters and letters, requires at least two words
    const nameRegex = /^[a-zA-ZçÇğĞıİöÖşŞüÜ\s]{2,50}$/;
    if (!nameRegex.test(name)) return false;
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2 && parts.every(p => p.length >= 2);
};

const validateTCKN = (tckn) => {
    if (!/^[1-9][0-9]{10}$/.test(tckn)) return false;
    const d = tckn.split('').map(Number);
    const sumOdd = d[0] + d[2] + d[4] + d[6] + d[8];
    const sumEven = d[1] + d[3] + d[5] + d[7];
    const digit10 = ((sumOdd * 7) - sumEven) % 10;
    if ((digit10 < 0 ? digit10 + 10 : digit10) !== d[9]) return false;
    const sumFirst10 = d.slice(0, 10).reduce((a, b) => a + b, 0);
    if (sumFirst10 % 10 !== d[10]) return false;
    return true;
};

const formatPhoneInput = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    // Automatically remove leading zero if exists
    if (digits.startsWith('0')) return digits.slice(1);
    return digits;
};

// Internal Password Input Component with Strength Meter
const PasswordInput = ({ value, onChange, placeholder, label, showStrength = false, showExternal, onToggleExternal }) => {
    const [internalShow, setInternalShow] = useState(false);

    const show = showExternal !== undefined ? showExternal : internalShow;
    const toggle = onToggleExternal || (() => setInternalShow(!internalShow));

    const calculateStrength = (pass) => {
        if (!pass) return { score: 0, label: '', color: 'bg-gray-100' };
        let score = 0;
        if (pass.length >= 8) score += 1;
        if (/[A-Z]/.test(pass) && /[a-z]/.test(pass) && /[0-9]/.test(pass)) score += 1;
        if (pass.length >= 12 && /[^A-Za-z0-9]/.test(pass)) score += 1;

        if (score === 0) return { score: 0, label: 'Zayıf', color: 'bg-gray-200' };
        if (score === 1) return { score: 1, label: 'Zayıf', color: 'bg-red-400' };
        if (score === 2) return { score: 2, label: 'Orta', color: 'bg-yellow-400' };
        return { score: 3, label: 'Güçlü', color: 'bg-green-500' };
    };

    const strength = calculateStrength(value);

    return (
        <div className="w-full">
            <FloatingLabelInput
                type={show ? 'text' : 'password'}
                label={label || placeholder}
                value={value}
                onChange={onChange}
                bgColor="bg-[#FAF7F0]"
                endIcon={
                    <button
                        type="button"
                        onClick={toggle}
                        tabIndex="-1"
                        className="text-gray-400 hover:text-[#D4AF37] transition-colors focus:outline-none"
                    >
                        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                }
            />

            {showStrength && (
                <div className="space-y-1 pt-2 animate-in slide-in-from-top-1 duration-300 px-1">
                    <div className="flex gap-1 h-1.5">
                        <div className={`flex-1 rounded-full transition-all duration-300 ${strength.score >= 1 ? strength.color : 'bg-gray-200'}`}></div>
                        <div className={`flex-1 rounded-full transition-all duration-300 ${strength.score >= 2 ? strength.color : 'bg-gray-200'}`}></div>
                        <div className={`flex-1 rounded-full transition-all duration-300 ${strength.score >= 3 ? strength.color : 'bg-gray-200'}`}></div>
                    </div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider text-right transition-colors duration-300 ${!value ? 'text-gray-400' :
                        strength.label === 'Zayıf' ? 'text-red-500' :
                            strength.label === 'Orta' ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                        {value ? strength.label : 'Güvenlik Durumu'}
                    </p>
                </div>
            )}
        </div>
    );
};

const LoginModal = () => {
    const { isLoginOpen, setIsLoginOpen, login, register, forgotPassword, checkAvailability } = useAuth();
    const { toast } = useToast();
    const analytics = useAnalytics();

    // State for View Mode: 'signIn', 'signUp', 'forgotPassword'
    const [view, setView] = useState('signIn');
    // 'signUp' activates the right panel. 'signIn' and 'forgotPassword' keep the left panel active.
    const isSignUpActive = view === 'signUp';

    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [rememberMe, setRememberMe] = useState(true);
    const [registerData, setRegisterData] = useState({
        name: '', email: '', password: '', confirmPassword: '',
        tckn: '', birthPlace: '', birthDate: '', motherName: '', fatherName: '', occupation: '',
        phone: '', address: '', city: '', district: ''
    });
    const [kvkkConsents, setKvkkConsents] = useState({
        kvkk: false, terms: false, acikRiza: false, marketing: false,
    });
    const [kvkkErrors, setKvkkErrors] = useState([]);
    const [forgotEmail, setForgotEmail] = useState('');
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showTCKN, setShowTCKN] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    // Memoized Cities & Districts for performance
    const cityOptions = useMemo(() =>
        citiesData.map(c => ({ value: c.city, label: c.city })),
        []);

    const districtOptions = useMemo(() => {
        if (!registerData.city) return [];
        const city = citiesData.find(c => c.city === registerData.city);
        return city ? city.districts.map(d => ({ value: d, label: d })) : [];
    }, [registerData.city]);

    const resetForms = () => {
        setLoginData({ email: '', password: '' });
        setRegisterData({
            name: '', email: '', password: '', confirmPassword: '',
            tckn: '', birthPlace: '', birthDate: '', motherName: '', fatherName: '', occupation: '',
            phone: '', address: '', city: '', district: ''
        });
        setKvkkConsents({ kvkk: false, terms: false, acikRiza: false, marketing: false });
        setKvkkErrors([]);
        setForgotEmail('');
        setRememberMe(false);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginData.email || !loginData.password) {
            toast({ title: 'Hata', description: 'Lütfen e-posta ve şifrenizi giriniz.', variant: 'destructive' });
            return;
        }
        setIsLoading(true);
        try {
            const result = await login(loginData.email, loginData.password, rememberMe);
            if (result.success) {
                toast({ title: 'Başarılı!', description: 'Giriş yapıldı.' });
                resetForms();
            } else {
                const isEmailError = result.error?.toLowerCase().includes('doğrulayın') || 
                                    result.error?.toLowerCase().includes('doğrula') ||
                                    result.error?.toLowerCase().includes('verify') ||
                                    result.error?.toLowerCase().includes('email');
                if (isEmailError) {
                    toast({
                        title: (
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                                <span className="font-bold">E-posta Doğrulaması Gerekli</span>
                            </div>
                        ),
                        description: (
                            <div className="space-y-2">
                                <p>Hesabınıza giriş yapabilmek için e-posta adresinizi doğrulamanız gerekmektedir.</p>
                                <p className="text-sm text-amber-700">Lütfen e-posta kutunuzu kontrol edin ve doğrulama linkine tıklayın.</p>
                            </div>
                        ),
                        variant: 'warning',
                        duration: 6000
                    });
                } else {
                    toast({ title: 'Hata', description: result.error || 'Giriş başarısız', variant: 'destructive' });
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!forgotEmail) {
            toast({ title: 'Hata', description: 'Lütfen e-posta adresinizi giriniz.', variant: 'destructive' });
            return;
        }
        setForgotLoading(true);
        try {
            let recaptchaToken = null;
            try {
                const { getRecaptchaToken } = await import('../utils/recaptcha');
                recaptchaToken = await getRecaptchaToken('forgot_password');
            } catch (e) {
                console.warn('reCAPTCHA error:', e);
            }

            const result = await forgotPassword(forgotEmail, recaptchaToken);
            if (result.success) {
                toast({ title: 'Başarılı!', description: result.message });
                toggleView('signIn');
            } else {
                toast({ title: 'Hata', description: result.error, variant: 'destructive' });
            }
        } finally {
            setForgotLoading(false);
        }
    };

    const toggleView = (newView) => {
        setView(newView);
        resetForms();
    };

    if (!isLoginOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/80 backdrop-blur-md z-[1000] flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-500"
                onClick={() => { setIsLoginOpen(false); resetForms(); }}
                data-scroll-lock="true"
            >
                <div
                    className={`bg-[#FAF7F0] rounded-[1.5rem] sm:rounded-[2.5rem] shadow-2xl relative overflow-hidden w-full max-w-[1200px] h-[95dvh] max-h-[95dvh] lg:h-auto lg:max-h-[90dvh] lg:min-h-[800px] transition-all duration-1000 ease-in-out ${isSignUpActive ? 'right-panel-active' : ''} form-container-wrapper border border-[#D4AF37]/20 flex flex-col`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 sm:top-8 sm:right-8 z-[100] p-2 sm:p-3 rounded-full bg-black/5 hover:bg-black/10 text-[#1a1a1a] transition-all duration-300"
                        onClick={() => { setIsLoginOpen(false); resetForms(); }}
                    >
                        <X className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>

                    {/* SIGN UP CONTAINER (Register) */}
                    <div className="form-container sign-up-container absolute top-0 h-full transition-all duration-1000 ease-in-out left-0 w-1/2 opacity-0 z-[1]">
                        <div className="h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-12 py-6 bg-[#FAF7F0] text-center">
                            <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 text-[#1a1a1a] shrink-0">Hesap Oluştur</h1>
                            <div className="w-full text-left overflow-y-auto max-h-[650px] custom-scrollbar px-4 sm:px-5 lg:px-6 pt-2">
                                <Stepper
                                    key={isSignUpActive ? 'active' : 'inactive'}
                                    initialStep={1}
                                    onBeforeNext={async (step) => {
                                        setIsVerifying(true);
                                        try {
                                            if (step === 1) {
                                                if (!registerData.name || !registerData.email || !registerData.password) {
                                                    toast({ title: 'Uyarı', description: 'Lütfen tüm alanları doldurun.', variant: 'destructive' });
                                                    return false;
                                                }
                                                if (!validateName(registerData.name)) {
                                                    toast({ title: 'Hata', description: 'Lütfen geçerli bir ad ve soyad giriniz.', variant: 'destructive' });
                                                    return false;
                                                }
                                                if (!validateEmail(registerData.email)) {
                                                    toast({ title: 'Hata', description: 'Lütfen geçerli bir e-posta adresi giriniz.', variant: 'destructive' });
                                                    return false;
                                                }
                                                if (registerData.password.length < 8) {
                                                    toast({ title: 'Hata', description: 'Şifre en az 8 karakter olmalıdır.', variant: 'destructive' });
                                                    return false;
                                                }
                                                const hasUpper = /[A-Z]/.test(registerData.password);
                                                const hasLower = /[a-z]/.test(registerData.password);
                                                const hasNumber = /\d/.test(registerData.password);
                                                if (!hasUpper || !hasLower || !hasNumber) {
                                                    toast({
                                                        title: 'Geçersiz Şifre',
                                                        description: 'Şifreniz en az bir büyük harf, bir küçük harf ve bir rakam içermelidir.',
                                                        variant: 'destructive',
                                                    });
                                                    return false;
                                                }
                                                if (registerData.password !== registerData.confirmPassword) {
                                                    toast({ title: 'Hata', description: 'Şifreler eşleşmiyor!', variant: 'destructive' });
                                                    return false;
                                                }

                                                // API Availability Check
                                                const check = await checkAvailability('email', registerData.email);
                                                if (!check.success) {
                                                    toast({ title: 'Uyarı', description: check.error, variant: 'warning' });
                                                    return false;
                                                }
                                                return true;
                                            }
                                            if (step === 2) {
                                                if (!registerData.tckn || !registerData.phone || !registerData.birthPlace || !registerData.birthDate || !registerData.motherName || !registerData.fatherName || !registerData.occupation) {
                                                    toast({ title: 'Uyarı', description: 'Lütfen tüm alanları doldurun.', variant: 'destructive' });
                                                    return false;
                                                }
                                                if (!validateTCKN(registerData.tckn)) {
                                                    toast({ title: 'Hata', description: 'Geçerli bir TC Kimlik numarası giriniz.', variant: 'destructive' });
                                                    return false;
                                                }
                                                if (registerData.phone.length !== 10) {
                                                    toast({ title: 'Hata', description: 'Telefon numarası 10 hane olmalıdır (5XX...).', variant: 'destructive' });
                                                    return false;
                                                }
                                                
                                                // 18 yaş kontrolü
                                                const birthDate = new Date(registerData.birthDate);
                                                const today = new Date();
                                                let age = today.getFullYear() - birthDate.getFullYear();
                                                const monthDiff = today.getMonth() - birthDate.getMonth();
                                                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                                                    age--;
                                                }
                                                
                                                if (age < 18) {
                                                    toast({ title: 'Uyarı', description: '18 yaşından küçükler üyelik oluşturamaz ve alışveriş yapamaz.', variant: 'destructive' });
                                                    return false;
                                                }

                                                // API Availability Checks
                                                const tcknCheck = await checkAvailability('tcKimlik', registerData.tckn);
                                                if (!tcknCheck.success) {
                                                    toast({ title: 'Uyarı', description: tcknCheck.error, variant: 'warning' });
                                                    return false;
                                                }
                                                const phoneCheck = await checkAvailability('phone', registerData.phone);
                                                if (!phoneCheck.success) {
                                                    toast({ title: 'Uyarı', description: phoneCheck.error, variant: 'warning' });
                                                    return false;
                                                }
                                                return true;
                                            }
                                            if (step === 3) {
                                                if (!registerData.city || !registerData.district || !registerData.address) {
                                                    toast({ title: 'Uyarı', description: 'Lütfen tüm adres bilgilerini doldurun.', variant: 'destructive' });
                                                    return false;
                                                }
                                                if (registerData.address.length < 10) {
                                                    toast({ title: 'Hata', description: 'Açık adres en az 10 karakter olmalıdır.', variant: 'destructive' });
                                                    return false;
                                                }
                                                return true;
                                            }
                                            if (step === 4) {
                                                const missingConsents = validateKVKKConsents(kvkkConsents);
                                                if (missingConsents.length > 0) {
                                                    setKvkkErrors(missingConsents);
                                                    toast({ title: 'Uyarı', description: 'Lütfen KVKK ve Kullanım Koşullarını kabul edin.', variant: 'destructive' });
                                                    return false;
                                                }
                                                return true;
                                            }
                                            return true;
                                        } finally {
                                            setIsVerifying(false);
                                        }
                                    }}
                                    onFinalStepCompleted={async () => {
                                        setIsLoading(true);
                                        try {
                                            const nameParts = registerData.name.trim().split(/\s+/);
                                            const firstName = nameParts[0];
                                            const lastName = nameParts.slice(1).join(' ') || '';

                                            const userData = {
                                                email: registerData.email,
                                                password: registerData.password,
                                                tcKimlik: registerData.tckn,
                                                firstName: firstName,
                                                lastName: lastName,
                                                phone: registerData.phone,
                                                birthPlace: registerData.birthPlace,
                                                birthDate: registerData.birthDate,
                                                motherName: registerData.motherName,
                                                fatherName: registerData.fatherName,
                                                occupation: registerData.occupation,
                                                address: registerData.address.trim(),
                                                city: registerData.city,
                                                district: registerData.district,
                                                kvkkConsent: kvkkConsents.kvkk,
                                                termsConsent: kvkkConsents.terms,
                                                acikRizaConsent: kvkkConsents.acikRiza,
                                                marketingConsent: kvkkConsents.marketing,
                                            };

                                            // reCAPTCHA v3
                                            try {
                                                const { getRecaptchaToken } = await import('../utils/recaptcha');
                                                const recaptchaToken = await getRecaptchaToken('register');
                                                if (recaptchaToken) {
                                                    userData.recaptchaToken = recaptchaToken;
                                                }
                                            } catch (e) {
                                                console.warn('reCAPTCHA error:', e);
                                            }

                                            const result = await register(userData);
                                            if (result.success) {
                                                // Track sign_up conversion
                                                analytics.trackEvent('sign_up', {
                                                    method: 'email'
                                                });
                                                
                                                toast({
                                                    title: 'Kayıt Başarılı!',
                                                    description: 'Hesabınız oluşturuldu. E-posta adresinizi doğruladıktan sonra giriş yapabilirsiniz.'
                                                });
                                                resetForms();
                                                setIsLoginOpen(false); // Close modal on success
                                                toggleView('signIn');
                                            } else {
                                                toast({ title: 'Hata', description: result.error || 'Kayıt başarısız', variant: 'destructive' });
                                            }
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }}
                                    backButtonText="Geri"
                                    nextButtonText={isVerifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "İleri"}
                                    finalStepButtonText={isLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Kayıt Ol"}
                                >
                                    {/* STEP 1: Personal Info */}
                                    <Step>
                                        <div className="space-y-3 sm:space-y-4 pt-2">
                                            <div className="space-y-1">
                                                <FloatingLabelInput
                                                    label="Ad Soyad *"
                                                    startIcon={<User className="w-4 h-4" />}
                                                    value={registerData.name}
                                                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                                                    required
                                                    bgColor="bg-[#FAF7F0]"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <FloatingLabelInput
                                                    label="E-Posta *"
                                                    type="email"
                                                    autoComplete="username"
                                                    startIcon={<Mail className="w-4 h-4" />}
                                                    value={registerData.email}
                                                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value.toLowerCase() })}
                                                    required
                                                    bgColor="bg-[#FAF7F0]"
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div className="space-y-1">
                                                    <PasswordInput
                                                        label="Şifre *"
                                                        autoComplete="new-password"
                                                        placeholder="En az 8 karakter"
                                                        value={registerData.password}
                                                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                                                        showStrength={true}
                                                        showExternal={showRegisterPassword}
                                                        onToggleExternal={() => setShowRegisterPassword(!showRegisterPassword)}
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <PasswordInput
                                                        label="Tekrar *"
                                                        autoComplete="new-password"
                                                        placeholder="Şifreyi doğrulayın"
                                                        value={registerData.confirmPassword}
                                                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                                                        showExternal={showRegisterPassword}
                                                        onToggleExternal={() => setShowRegisterPassword(!showRegisterPassword)}
                                                    />
                                                </div>
                                            </div>
                                             <p className="text-[10px] sm:text-[11px] text-gray-500 text-center font-medium mt-1 sm:mt-2">
                                                 🔒 Şifreniz en az 8 karakter, en az bir büyük harf, bir küçük harf ve bir rakam içermelidir.
                                             </p>
                                        </div>
                                    </Step>

                                    {/* STEP 2: TC Kimlik ve Doğum Bilgileri */}
                                    <Step>
                                        <div className="space-y-3 sm:space-y-4 pt-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div className="space-y-1">
                                                    <FloatingLabelInput
                                                        label="TCKN *"
                                                        type="text"
                                                        startIcon={<CreditCard className="w-4 h-4" />}
                                                        maxLength="11"
                                                        value={registerData.tckn}
                                                        onChange={(e) => setRegisterData({ ...registerData, tckn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                                                        required
                                                        bgColor="bg-[#FAF7F0]"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <FloatingLabelInput
                                                        label="Telefon *"
                                                        type="tel"
                                                        startIcon={<Phone className="w-4 h-4" />}
                                                        value={registerData.phone}
                                                        onChange={(e) => setRegisterData({ ...registerData, phone: formatPhoneInput(e.target.value) })}
                                                        required
                                                        bgColor="bg-[#FAF7F0]"
                                                        placeholder="5XX XXX XX XX"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div className="space-y-1">
                                                    <FloatingLabelInput
                                                        label="Doğum Yeri *"
                                                        startIcon={<MapPin className="w-4 h-4" />}
                                                        value={registerData.birthPlace}
                                                        onChange={(e) => setRegisterData({ ...registerData, birthPlace: e.target.value })}
                                                        required
                                                        bgColor="bg-[#FAF7F0]"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <FloatingLabelInput
                                                        label="Doğum Tarihi *"
                                                        type="date"
                                                        value={registerData.birthDate}
                                                        onChange={(e) => setRegisterData({ ...registerData, birthDate: e.target.value })}
                                                        required
                                                        bgColor="bg-[#FAF7F0]"
                                                        className="date-input-hidden-placeholder"
                                                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div className="space-y-1">
                                                    <FloatingLabelInput
                                                        label="Anne Adı *"
                                                        startIcon={<User className="w-4 h-4" />}
                                                        value={registerData.motherName}
                                                        onChange={(e) => setRegisterData({ ...registerData, motherName: e.target.value })}
                                                        required
                                                        bgColor="bg-[#FAF7F0]"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <FloatingLabelInput
                                                        label="Baba Adı *"
                                                        startIcon={<User className="w-4 h-4" />}
                                                        value={registerData.fatherName}
                                                        onChange={(e) => setRegisterData({ ...registerData, fatherName: e.target.value })}
                                                        required
                                                        bgColor="bg-[#FAF7F0]"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <FloatingLabelInput
                                                    label="Meslek *"
                                                    value={registerData.occupation}
                                                    onChange={(e) => setRegisterData({ ...registerData, occupation: e.target.value })}
                                                    required
                                                    bgColor="bg-[#FAF7F0]"
                                                />
                                            </div>
                                        </div>
                                    </Step>

                                    {/* STEP 3: Adres Bilgileri */}
                                    <Step>
                                        <div className="space-y-3 sm:space-y-4 pt-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <SearchableSelect
                                                    label="İl *"
                                                    options={cityOptions}
                                                    value={registerData.city}
                                                    onChange={(val) => setRegisterData({ ...registerData, city: val, district: '' })}
                                                    placeholder="İl Seçiniz"
                                                    searchPlaceholder="İl Ara..."
                                                />
                                                <SearchableSelect
                                                    label="İlçe *"
                                                    options={districtOptions}
                                                    value={registerData.district}
                                                    onChange={(val) => setRegisterData({ ...registerData, district: val })}
                                                    placeholder="İlçe Seçiniz"
                                                    searchPlaceholder="İlçe Ara..."
                                                    disabled={!registerData.city}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-[10px] font-bold text-gray-600 uppercase tracking-widest ml-1 mb-1 block">Açık Adres *</Label>
                                                <textarea
                                                    className="w-full bg-[#FAF7F0] border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/20 rounded-xl p-3 text-xs min-h-[70px] outline-none transition-all resize-none placeholder:text-gray-400"
                                                    value={registerData.address}
                                                    onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                                                    required
                                                    placeholder="Mahalle, Sokak, No, Daire..."
                                                />
                                            </div>
                                        </div>
                                    </Step>

                                    {/* STEP 4: Consents & KVKK */}
                                    <Step>
                                        <div className="space-y-2 pt-3 sm:pt-6 px-1 sm:px-2">
                                            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4">
                                                <div className="flex items-center gap-3 text-[#D4AF37] mb-2">
                                                    <AlertTriangle className="w-5 h-5 shrink-0" />
                                                    <h3 className="font-bold text-sm tracking-tight">Son Adım: Bilgilendirme ve Onaylar</h3>
                                                </div>
                                                <p className="text-[11px] text-gray-600 leading-relaxed">
                                                    Carmen Gold olarak güvenliğinize önem veriyoruz. Lütfen aşağıdaki yasal bilgilendirmeleri okuyup onaylayarak kaydınızı tamamlayınız.
                                                </p>
                                            </div>

                                            <KVKKCheckboxes
                                                consents={kvkkConsents}
                                                onChange={(key, value) => {
                                                    setKvkkConsents(prev => ({ ...prev, [key]: value }));
                                                    if (kvkkErrors.includes(key) && value) {
                                                        setKvkkErrors(prev => prev.filter(e => e !== key));
                                                    }
                                                }}
                                                errors={kvkkErrors}
                                            />
                                        </div>
                                    </Step>
                                </Stepper>
                            </div>
                        </div>
                    </div>

                    {/* SIGN IN / FORGOT PASSWORD CONTAINER */}
                    <div className="form-container sign-in-container absolute top-0 h-full transition-all duration-1000 ease-in-out left-0 w-1/2 z-[2] overflow-y-auto">
                        <div className="h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-16 py-4 sm:py-6 lg:py-12 bg-[#FAF7F0] text-center relative">

                            {view === 'signIn' ? (
                                <div className="w-full max-w-md animate-in fade-in zoom-in-95 duration-500">
                                    <div className="mb-4 sm:mb-6 lg:mb-8">
                                        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-5xl font-bold text-[#1a1a1a] mb-2 lg:mb-3">Hoş Geldiniz</h1>
                                        <p className="text-sm sm:text-base text-[#8A8A8A] font-['Manrope']">Hesabınıza giriş yaparak ayrıcalıkları keşfedin</p>
                                    </div>

                                    <form onSubmit={handleLogin} className="w-full space-y-3 sm:space-y-4">
                                        <div className="text-left space-y-1">
                                            <FloatingLabelInput
                                                label="E-Posta"
                                                type="email"
                                                startIcon={<Mail className="w-4 h-4" />}
                                                value={loginData.email}
                                                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                                required
                                                bgColor="bg-[#FAF7F0]"
                                            />
                                        </div>

                                        <div className="text-left space-y-1">
                                            <PasswordInput
                                                label="Şifre"
                                                value={loginData.password}
                                                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                            />
                                            <div className="flex justify-between items-center mt-3 px-1">
                                                <div className="flex items-center gap-2" style={{ marginTop: '10px' }}>
                                                    <input
                                                        id="rememberMe"
                                                        type="checkbox"
                                                        checked={rememberMe}
                                                        onChange={(e) => setRememberMe(e.target.checked)}
                                                        className="w-4 h-4 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37] cursor-pointer"
                                                    />
                                                    <label htmlFor="rememberMe" className="text-xs text-gray-600 font-medium cursor-pointer select-none">Beni Hatırla</label>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleView('forgotPassword')}
                                                    className="text-xs text-[#D4AF37] font-bold hover:underline hover:text-[#B4941F] transition-colors"
                                                >
                                                    Şifremi Unuttum
                                                </button>
                                            </div>
                                        </div>

                                        <Button
                                            type="submit"
                                            disabled={isLoading}
                                            className="w-full h-11 sm:h-12 bg-[#D4AF37] hover:bg-[#B4941F] text-white font-bold tracking-wide rounded-xl sm:rounded-2xl transition-all duration-300 shadow-xl hover:shadow-[#D4AF37]/30 hover:-translate-y-1 mt-3 sm:mt-4 text-base sm:text-lg"
                                        >
                                            {isLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Giriş Yap"}
                                        </Button>
                                    </form>
                                </div>
                            ) : (
                                <div className="w-full max-w-md animate-in fade-in slide-in-from-left-4 duration-500">
                                    <button
                                        onClick={() => toggleView('signIn')}
                                        className="absolute top-4 sm:top-6 lg:top-12 left-4 sm:left-6 lg:left-12 p-2 lg:p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-[#1a1a1a] hover:text-white transition-all shadow-sm group"
                                    >
                                        <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5 group-hover:-translate-x-1 transition-transform" />
                                    </button>

                                    <div className="mb-4 sm:mb-6 lg:mb-8">
                                        <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6 text-[#D4AF37]">
                                            <KeyRound className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                                        </div>
                                        <h1 className="font-['Playfair_Display'] text-xl sm:text-2xl lg:text-4xl font-bold text-[#1a1a1a] mb-2 lg:mb-3">Şifre Sıfırlama</h1>
                                        <p className="text-sm sm:text-base text-[#8A8A8A] font-['Manrope'] px-2">
                                            E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.
                                        </p>
                                    </div>

                                    <form onSubmit={handleForgotPassword} className="w-full space-y-4 sm:space-y-6">
                                        <div className="text-left space-y-2">
                                            <FloatingLabelInput
                                                label="E-Posta"
                                                type="email"
                                                startIcon={<Mail className="w-4 h-4" />}
                                                value={forgotEmail}
                                                onChange={(e) => setForgotEmail(e.target.value)}
                                                required
                                                bgColor="bg-[#FAF7F0]"
                                            />
                                        </div>

                                        <Button
                                            type="submit"
                                            disabled={forgotLoading}
                                            className="w-full h-11 sm:h-12 bg-[#1a1a1a] hover:bg-[#D4AF37] text-white font-bold tracking-wide rounded-xl sm:rounded-2xl transition-all duration-300 shadow-xl hover:-translate-y-1 text-base sm:text-lg group"
                                        >
                                            {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-white" /> : <span className="group-hover:text-white transition-colors">Bağlantı Gönder</span>}
                                        </Button>
                                    </form>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* OVERLAY CONTAINER */}
                    <div className="overlay-container overflow-hidden z-[10]">
                        <div className="overlay bg-gradient-to-br from-[#D4AF37] to-[#8B7322] text-white">

                            {/* Left Overlay Panel */}
                            <div className="overlay-panel overlay-left flex flex-col items-center justify-center px-6 lg:px-12 text-center">
                                <h1 className="font-['Playfair_Display'] font-bold leading-tight relative z-10 w-full">Tekrar Hoş Geldiniz</h1>
                                <p className="font-light text-white/90 leading-relaxed font-['Manrope'] relative z-10">
                                    Mevcut hesabınızla giriş yaparak alışverişe kaldığınız yerden devam edebilir, siparişlerinizi takip edebilirsiniz.
                                </p>
                                <button
                                    className="rounded-full border-2 border-white bg-transparent text-white font-bold uppercase tracking-widest transition-all hover:bg-white hover:text-[#D4AF37] hover:scale-105 hover:shadow-2xl relative z-10"
                                    onClick={() => toggleView('signIn')}
                                >
                                    Giriş Yap
                                </button>
                            </div>

                            {/* Right Overlay Panel */}
                            <div className="overlay-panel overlay-right flex flex-col items-center justify-center px-6 lg:px-12 text-center">
                                <h1 className="font-['Playfair_Display'] font-bold leading-tight relative z-10 w-full">Aramıza Katılın</h1>
                                <p className="font-light text-white/90 leading-relaxed font-['Manrope'] relative z-10">
                                    Carmen Gold dünyasına adım atın. Özel fırsatlardan, güvenli yatırım seçeneklerinden ve size özel kampanyalardan yararlanın.
                                </p>
                                <button
                                    className="rounded-full border-2 border-white bg-transparent text-white font-bold uppercase tracking-widest transition-all hover:bg-white hover:text-[#D4AF37] hover:scale-105 hover:shadow-2xl relative z-10"
                                    onClick={() => toggleView('signUp')}
                                >
                                    Kayıt Ol
                                </button>
                            </div>

                        </div>
                    </div>

                </div>
            </div>

            <style>{`
        /* ========= COMMON RESPONSIVE TEXT STYLES ========= */
        .overlay-panel h1 { font-size: 1.75rem; margin-bottom: 1rem; }
        .overlay-panel p { font-size: 0.875rem; margin-bottom: 1.5rem; padding: 0 1rem; }
        .overlay-panel button { padding: 0.75rem 2rem; font-size: 0.75rem; }
        
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E5E5E5; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #D4AF37; }

        @media (min-width: 1024px) {
          .overlay-panel h1 { font-size: 3rem; margin-bottom: 1.5rem; }
          .overlay-panel p { font-size: 1.125rem; margin-bottom: 2.5rem; padding: 0; }
          .overlay-panel button { padding: 1rem 3rem; font-size: 0.875rem; }
        }

        /* ========= MOBILE (max-width: 1023px) ========= */
        @media (max-width: 1023px) {
          .form-container-wrapper .sign-up-container,
          .form-container-wrapper .sign-in-container {
            position: absolute;
            left: 0;
            width: 100%;
            height: 60%;
            transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .sign-up-container {
            top: 40%;
            transform: translateY(100%);
            opacity: 0;
            z-index: 1;
            pointer-events: none;
          }

          .form-container-wrapper .sign-in-container {
            top: 0;
            transform: translateY(0);
            opacity: 1;
            z-index: 2;
          }

          .form-container-wrapper .overlay-container {
            position: absolute;
            left: 0;
            top: 60%;
            width: 100%;
            height: 40%;
            transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .overlay {
            background-image: url('/login-bg.png');
            background-size: cover;
            background-position: center;
            position: absolute;
            left: 0;
            top: -100%;
            width: 100%;
            height: 200%;
            transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .overlay-panel {
            position: absolute;
            width: 100%;
            height: 50%;
            transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .overlay-left {
            top: 0;
            left: 0;
            transform: translateY(-20%);
          }

          .form-container-wrapper .overlay-right {
            top: 50%;
            left: 0;
            transform: translateY(0);
          }

          /* Active (Sign Up) STATES (MOBILE) */
          .form-container-wrapper.right-panel-active .sign-in-container {
            transform: translateY(-100%);
            opacity: 0;
            pointer-events: none;
          }
          
          .form-container-wrapper.right-panel-active .sign-up-container {
            transform: translateY(0);
            opacity: 1;
            z-index: 5;
            pointer-events: auto;
          }
          
          .form-container-wrapper.right-panel-active .overlay-container {
            transform: translateY(-150%);
          }
          
          .form-container-wrapper.right-panel-active .overlay {
            transform: translateY(50%);
          }
          
          .form-container-wrapper.right-panel-active .overlay-left {
            transform: translateY(0);
          }
          
          .form-container-wrapper.right-panel-active .overlay-right {
            transform: translateY(20%);
          }
        }

        /* ========= DESKTOP (min-width: 1024px) ========= */
        @media (min-width: 1024px) {
          .form-container-wrapper .sign-up-container,
          .form-container-wrapper .sign-in-container {
            position: absolute;
            top: 0;
            width: 50%;
            height: 100%;
            transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .sign-up-container {
            left: 0;
            opacity: 0;
            z-index: 1;
            pointer-events: none;
          }

          .form-container-wrapper .sign-in-container {
            left: 0;
            z-index: 2;
            opacity: 1;
          }

          .form-container-wrapper .overlay-container {
            position: absolute;
            top: 0;
            left: 50%;
            width: 50%;
            height: 100%;
            overflow: hidden;
            z-index: 10;
            transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .overlay {
            background-image: url('/login-bg.png');
            background-size: cover;
            background-position: center;
            position: relative;
            left: -100%;
            height: 100%;
            width: 200%;
            transform: translateX(0);
            transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .overlay-panel {
            position: absolute;
            top: 0;
            height: 100%;
            width: 50%;
            transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-container-wrapper .overlay-left {
            left: 0;
            transform: translateX(-20%);
          }

          .form-container-wrapper .overlay-right {
            right: 0;
            transform: translateX(0);
          }

          /* Active (Sign Up) States Desktop */
          .form-container-wrapper.right-panel-active .sign-in-container {
            transform: translateX(100%);
            opacity: 0;
            pointer-events: none;
          }
          
          .form-container-wrapper.right-panel-active .sign-up-container {
            transform: translateX(100%);
            opacity: 1;
            z-index: 5;
            pointer-events: auto;
          }
          
          .form-container-wrapper.right-panel-active .overlay-container {
            transform: translateX(-100%);
          }
          
          .form-container-wrapper.right-panel-active .overlay {
            transform: translateX(50%);
          }
          
          .form-container-wrapper.right-panel-active .overlay-left {
            transform: translateX(0);
          }
          
          .form-container-wrapper.right-panel-active .overlay-right {
            transform: translateX(20%);
          }
        }
      `}</style>
        </>
    );
};

export default LoginModal;
